import AppButton from '@nxsys/components/AppButton';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { Icons } from '@nxsys/types/icons';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetPensionProviders,
  useUpdateCompanyById,
} from '@nxsys/hooks/api/company';
import AppLoader from '@nxsys/components/AppLoader';
import RatesThreshold from './components/RatesThreshold';
import PensionForm, {
  PensionFormRef,
  PensionValues,
} from './components/PensionForm';
import {
  defaultAEPostponementDaysOptions,
  defaultPensionMethodOptions,
} from '@nxsys/constants/AppConstants';
import {
  isNowLikePensionProvider,
  PensionProvidersNameEnum,
} from '@nxsys/constants/AppEnums';
import { pensionCalCulationMethodOptions } from '@nxsys/utils';
import { toNumber } from 'lodash';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
  fetchCompany: () => void;
};

enum PensionTabEnum {
  PENSION_SETTINGS = 'PENSION_SETTINGS',
  RATES_THRESHOLDS = 'RATES_THRESHOLDS',
}

const PensionTab: React.FC<Props> = ({ data, updateData, fetchCompany }) => {
  const { messages } = useTypedIntl();
  const formRef = useRef<PensionFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    value: pensionProvidersValue,
    loading: isFetchingPensionProviders,
    run: getPensionProviders,
  } = useGetPensionProviders();
  const pensionProviders = useMemo(
    () => pensionProvidersValue?.pensionProviders,
    [pensionProvidersValue],
  );

  useEffect(() => {
    if (data?.id) getPensionProviders(data.id);
  }, [data?.id]);

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.pensionSettings'],
        key: PensionTabEnum.PENSION_SETTINGS,
      },
      {
        tab: messages['common.ratesThresholds'],
        key: PensionTabEnum.RATES_THRESHOLDS,
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<PensionTabEnum>(
    PensionTabEnum.PENSION_SETTINGS,
  );

  const handleTabChange = (key: PensionTabEnum) => {
    setActiveTab(key);
  };

  // A manual NEST company saves in two calls — the company record, then its own
  // settings endpoint — so the page must not claim success until both land.
  // That case updates the company without `updateData`'s success toast and
  // hands the result to PensionForm, which owns the message. Every other
  // provider keeps going through `updateData` exactly as before.
  const { run: updateCompanyForNest } = useUpdateCompanyById();

  const handleSubmit = useCallback(
    async (values: PensionValues) => {
      setIsUpdating(true);
      try {
        let params: UpdateCompanyByIdParams = {};
        const selectedProvider = values.pensionProviderId?.value ?? '';
        const autoEnrolSettingsId = data?.autoEnrolSettings?.id;
        if (selectedProvider === PensionProvidersNameEnum.SMART_PENSION) {
          params = {
            autoEnrolSettings: {
              autoEnrolSettingsId,
              pensionProviderId: values.pensionProviderId?.value || null,
              pensionCalculationType:
                values.pensionCalculationType?.value || 'NET_PAY_ARRANGEMENT',
              pensionMethod: values.pensionMethod?.value || 'QUALIFYING_EARNINGS',
              // Smart runs through its API only: scheme, postponement and
              // rates come from Smart, and saving clears any stale manual flag.
              managePensionManually: false,
              isSalarySacrifice: values.isSalarySacrifice ?? null,
              pensionSelfCertification: values.pensionSelfCertification || null,
            } as any,
          };
        } else if (selectedProvider === PensionProvidersNameEnum.NEST_PENSION) {
          params = {
            autoEnrolSettings: {
              autoEnrolSettingsId,
              pensionProviderId: values.pensionProviderId?.value || null,
              aePostponementDays: values.aePostponementDays?.value || null,
              managePensionManually: !!values.managePensionManually,
              isSalarySacrifice: values.isSalarySacrifice ?? null,
              // NX-5231: NEST certifies per payment group — the set is saved
              // on each frequency's settings via updateNestSetting, not here.
            } as any,
            nestUsername: values.nestUsername || '',
            nestPassword: values.nestPassword || '',
            nestEmployerRef: values.nestEmployerRef || '',
            nestDelegateId: values.nestDelegateId || '',
          };
        } else if (selectedProvider === PensionProvidersNameEnum.JARVIS_PENSION) {
          params = {
            autoEnrolSettings: {
              autoEnrolSettingsId,
              pensionProviderId: values.pensionProviderId?.value || null,
              erCode: values.erCode || '',
              pensionCalculationType:
                values.pensionCalculationType?.value || 'NET_PAY_ARRANGEMENT',
              payCode: values.payCode || '',
              pensionMethod: values.pensionMethod?.value || 'QUALIFYING_EARNINGS',
              pensionSelfCertification: values.pensionSelfCertification || null,
              schemeCode: values.schemeCode || '',
              isSalarySacrifice: values.isSalarySacrifice ?? null,
              schemeCode2Weekly: values.schemeCode2Weekly || '',
              aePostponementDays: values.aePostponementDays?.value || null,
              schemeCode4Weekly: values.schemeCode4Weekly || '',
              schemeCodeMonthly: values.schemeCodeMonthly || '',
              erPensionPercentage: values.erPensionPercentage
                ? toNumber(values.erPensionPercentage)
                : null,
              eePensionPercentage: values.eePensionPercentage
                ? toNumber(values.eePensionPercentage)
                : null,
              jarvisGroupOrganizationApiKey:
                values.jarvisGroupOrganizationApiKey || '',
              jarvisOrganizationID: values.jarvisOrganizationID || '',
              jarvisPensionScheme: values.jarvisPensionScheme || '',
            } as any,
          };
        } else if (isNowLikePensionProvider(selectedProvider)) {
          params = {
            autoEnrolSettings: {
              autoEnrolSettingsId,
              pensionProviderId: values.pensionProviderId?.value || null,
              erCode: values.erCode || '',
              pensionCalculationType:
                values.pensionCalculationType?.value || 'NET_PAY_ARRANGEMENT',
              payCode: values.payCode || '',
              pensionMethod: values.pensionMethod?.value || 'QUALIFYING_EARNINGS',
              pensionSelfCertification: values.pensionSelfCertification || null,
              schemeCode: values.schemeCode || '',
              isSalarySacrifice: values.isSalarySacrifice ?? null,
              schemeCode2Weekly: values.schemeCode2Weekly || '',
              aePostponementDays: values.aePostponementDays?.value || null,
              schemeCode4Weekly: values.schemeCode4Weekly || '',
              schemeCodeMonthly: values.schemeCodeMonthly || '',
              erPensionPercentage: values.erPensionPercentage
                ? toNumber(values.erPensionPercentage)
                : null,
              eePensionPercentage: values.eePensionPercentage
                ? toNumber(values.eePensionPercentage)
                : null,
            },
          };
        }
        // Only the manual case: that is the one where PensionForm follows up
        // with the NEST settings call and owns the verdict. A connected NEST
        // company saves in one call and keeps updateData's toast.
        const isManualNest =
          selectedProvider === PensionProvidersNameEnum.NEST_PENSION &&
          !!data?.autoEnrolSettings?.managePensionManually;
        if (isManualNest) {
          const res = await updateCompanyForNest(data?.id ?? '', params);
          if (!res?.success) return false;
          fetchCompany();
          return true;
        }
        await updateData(params);
      } finally {
        setIsUpdating(false);
      }
    },
    [data, updateData, updateCompanyForNest, fetchCompany],
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case PensionTabEnum.PENSION_SETTINGS:
        if (!data || !pensionProviders) return null;
        return (
          <PensionForm
            formRef={formRef}
            onSubmit={handleSubmit}
            defaultValues={{
              pensionProviderId:
                pensionProviders?.find(
                  (item) =>
                    item.value === data?.autoEnrolSettings?.pensionProviderId,
                ) || null,
              pensionCalculationType:
                pensionCalCulationMethodOptions.find(
                  (item) =>
                    item.value ===
                    data?.autoEnrolSettings?.pensionCalculationType,
                ) || null,
              payCode: data?.autoEnrolSettings?.payCode || '',
              pensionMethod:
                defaultPensionMethodOptions.find(
                  (item) =>
                    item.value === data?.autoEnrolSettings?.pensionMethod,
                ) || null,
              schemeCode: data?.autoEnrolSettings?.schemeCode || '',
              isSalarySacrifice:
                data?.autoEnrolSettings?.isSalarySacrifice ?? null,
              schemeCode2Weekly:
                data?.autoEnrolSettings?.schemeCode2Weekly || '',
              aePostponementDays:
                defaultAEPostponementDaysOptions.find(
                  (item) =>
                    item.value === data?.autoEnrolSettings?.aePostponementDays,
                ) || null,
              schemeCode4Weekly:
                data?.autoEnrolSettings?.schemeCode4Weekly || '',
              schemeCodeMonthly:
                data?.autoEnrolSettings?.schemeCodeMonthly || '',
              pensionSchemeName:
                data?.autoEnrolSettings?.pensionSchemeName || '',
              erPensionPercentage: (
                data?.autoEnrolSettings?.erPensionPercentage ?? 3
              ).toString(),
              eePensionPercentage: (
                data?.autoEnrolSettings?.eePensionPercentage ?? 5
              ).toString(),
              pensionSelfCertification:
                data?.autoEnrolSettings?.pensionSelfCertification || '',
              nestUsername: data?.nestUsername || '',
              nestPassword: data?.nestPassword || '',
              nestEmployerRef: data?.nestEmployerRef || '',
              nestDelegateId: data?.nestDelegateId || '',
              nestConnectionStatus: data?.nestConnectionStatus || '',
              nestConnectDoneAt: data?.nestConnectDoneAt || '',
              jarvisGroupOrganizationApiKey:
                (data?.autoEnrolSettings as any)
                  ?.jarvisGroupOrganizationApiKey || '',
              jarvisOrganizationID:
                (data?.autoEnrolSettings as any)?.jarvisOrganizationID || '',
              jarvisPensionScheme:
                (data?.autoEnrolSettings as any)?.jarvisPensionScheme || '',
              smartPensionCompanyId: data?.smartPensionCompanyId || '',
              pensionSchemeId:
                data?.autoEnrolSettings?.pensionSchemeId || '',
              managePensionManually:
                data?.autoEnrolSettings?.managePensionManually ?? false,
            }}
            pensionProviderOptions={pensionProviders ?? []}
            companyData={data!}
            fetchCompany={fetchCompany as any}
          />
        );
      case PensionTabEnum.RATES_THRESHOLDS:
        return <RatesThreshold />;
      default:
        return <></>;
    }
  };

  return (
    <div className="PensionTab relative">
      {isFetchingPensionProviders && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{renderTabContent()}</div>
      </AppCardTabListContainer>
      {activeTab === PensionTabEnum.PENSION_SETTINGS && (
        <div className="flex items-center justify-start gap-3 px-[16px]">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle
                color={defaultTheme.theme.palette.white}
              />
            }
            onClick={() => {
              formRef.current?.submit();
            }}
            loading={isUpdating}
            disabled={isUpdating}
          />
        </div>
      )}
    </div>
  );
};

export default PensionTab;
